import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Package, LogOut, User as UserIcon, MapPin } from "lucide-react";
import { formatIDR } from "@/lib/format";
import { useLang } from "@/context/LanguageContext";
import AddressBook from "@/components/AddressBook";

function StatusPill({ status, t }) {
  const map = {
    pending: "bg-amber-100 text-amber-800",
    paid: "bg-blue-100 text-blue-800",
    shipped: "bg-indigo-100 text-indigo-800",
    delivered: "bg-emerald-100 text-emerald-800",
    cancelled: "bg-zinc-100 text-zinc-600",
    refunded: "bg-zinc-100 text-zinc-600",
  };
  const label = t(`status.${status}`, status);
  return <span className={`text-[10px] font-bold uppercase tracking-[0.15em] px-2.5 py-1 rounded-full ${map[status] || "bg-zinc-100 text-zinc-600"}`}>{label}</span>;
}

export default function Account() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState("orders");

  useEffect(() => {
    api.get("/orders/mine").then((r) => setOrders(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <main data-testid="account-page" className="max-w-6xl mx-auto px-6 md:px-10 py-12">
      <div className="grid md:grid-cols-4 gap-8">
        <aside className="md:col-span-1">
          <div className="bg-white rounded-3xl border border-zinc-100 p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-brand text-white flex items-center justify-center font-display font-bold text-lg">
                {user?.name?.[0]?.toUpperCase() || "U"}
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-zinc-900 truncate">{user?.name}</div>
                <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
              </div>
            </div>
            <div className="mt-6 space-y-1">
              <button data-testid="account-orders-link" onClick={() => setSection("orders")} className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm ${section === "orders" ? "bg-zinc-100 text-zinc-900 font-semibold" : "text-zinc-700 hover:bg-zinc-50 font-medium"}`}>
                <Package className="w-4 h-4" /> {t("account.orders")}
              </button>
              <button data-testid="account-addresses-link" onClick={() => setSection("addresses")} className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm ${section === "addresses" ? "bg-zinc-100 text-zinc-900 font-semibold" : "text-zinc-700 hover:bg-zinc-50 font-medium"}`}>
                <MapPin className="w-4 h-4" /> {t("account.addresses")}
              </button>
              {user?.role === "admin" && (
                <button data-testid="account-admin-link" onClick={() => navigate("/adminX")} className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-zinc-700 hover:bg-zinc-50 text-sm font-medium">
                  <UserIcon className="w-4 h-4" /> {t("account.adminPanel")}
                </button>
              )}
              <button data-testid="account-logout-btn" onClick={async () => { await logout(); navigate("/"); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-zinc-700 hover:bg-zinc-50 text-sm font-medium">
                <LogOut className="w-4 h-4" /> {t("account.logOut")}
              </button>
            </div>
          </div>
        </aside>

        <section className="md:col-span-3">
          {section === "addresses" ? (
            <AddressBook />
          ) : (
            <>
          <h1 className="font-display font-extrabold text-3xl tracking-tighter text-zinc-900 mb-6">{t("account.yourOrders")}</h1>
          {loading ? (
            <div className="text-sm text-zinc-500">{t("account.loading")}</div>
          ) : orders.length === 0 ? (
            <div className="bg-white rounded-3xl border border-zinc-100 p-10 text-center">
              <Package className="w-8 h-8 text-zinc-300 mx-auto" />
              <div className="mt-3 font-semibold text-zinc-900">{t("account.noOrders")}</div>
              <div className="mt-1 text-sm text-zinc-500">{t("account.noOrdersHint")}</div>
              <Button data-testid="empty-orders-shop-btn" onClick={() => navigate("/shop")} className="mt-5 rounded-full bg-brand hover:bg-brand-hover text-white px-6">{t("account.startShopping")}</Button>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((o) => (
                <div key={o.id} className="bg-white rounded-3xl border border-zinc-100 p-6" data-testid={`order-${o.id}`}>
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">{t("account.order")}</div>
                      <div className="font-mono text-sm text-zinc-900">{o.id}</div>
                    </div>
                    <div className="text-sm text-zinc-500">{new Date(o.created_at).toLocaleDateString()}</div>
                    <StatusPill status={o.status} t={t} />
                    <div className="font-display font-bold text-lg text-zinc-900">{formatIDR(o.total)}</div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-zinc-100 flex gap-3 overflow-x-auto">
                    {o.items.map((it) => (
                      <div key={it.product_id} className="flex items-center gap-3 min-w-[240px]">
                        <div className="w-14 h-14 rounded-lg product-image-bg overflow-hidden flex-shrink-0">
                          <img src={it.image_url} alt={it.name} className="w-full h-full object-cover" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-zinc-900 truncate">{it.name}</div>
                          <div className="text-xs text-zinc-500">{t("account.qty")} {it.quantity} · {formatIDR(it.price)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
            </>
          )}
        </section>
      </div>
    </main>
  );
}
